import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getGlobalNotifications,
  markAllReadNotifications,
  markReadNotification,
} from 'redux/notificationSlice';
import { toast } from 'react-toastify';
import { FaBell, FaCheckDouble, FaExternalLinkAlt } from 'react-icons/fa';
import DateTimeToText from 'helpers/dateTimetoText';
import createMarkup from 'helpers/createMarkup';
import { EmployeeAvatar } from 'components/common/select/EmployeeBadge';

// ── Single notification item ──────────────────────────────────────────────────
const NotificationItem = ({ id, message, url, unread, avatar, created_at, onNavigate }) => {
  const dispatch = useDispatch();

  const handleClick = async () => {
    if (unread) {
      try { await dispatch(markReadNotification(id)).unwrap(); } catch { /* ignore */ }
    }
    if (url) {
      onNavigate(url);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`tw-w-full tw-flex tw-items-start tw-gap-3 tw-px-4 tw-py-3 tw-border-0 tw-text-left tw-cursor-pointer tw-transition-colors ${
        unread ? 'tw-bg-blue-50/60 hover:tw-bg-blue-50' : 'tw-bg-white hover:tw-bg-slate-50'
      }`}
    >
      {/* Avatar */}
      <div className="tw-shrink-0 tw-mt-0.5">
        <EmployeeAvatar name={avatar?.name || '?'} avatarUrl={avatar?.avatar_url} size={32} fontSize={12} />
      </div>

      {/* Content */}
      <div className="tw-flex-1 tw-min-w-0">
        <p className="tw-text-xs tw-text-slate-700 tw-mb-0.5 tw-leading-snug notification-msg"
          dangerouslySetInnerHTML={createMarkup(message)} />
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-[10px] tw-text-slate-400">
            <DateTimeToText datetime={created_at} />
          </span>
          {url && (
            <FaExternalLinkAlt size={9} className="tw-text-slate-300" />
          )}
        </div>
      </div>

      {/* Unread dot */}
      {unread && (
        <span className="tw-w-2 tw-h-2 tw-rounded-xl tw-bg-blue-500 tw-shrink-0 tw-mt-1.5" />
      )}
    </button>
  );
};

NotificationItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  message: PropTypes.string,
  url: PropTypes.string,
  unread: PropTypes.bool,
  avatar: PropTypes.shape({
    name: PropTypes.string,
    avatar_url: PropTypes.string
  }),
  created_at: PropTypes.string,
  onNavigate: PropTypes.func.isRequired
};

// ── Dropdown ──────────────────────────────────────────────────────────────────
const NotificationDropdown = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const triggerRef = useRef(null);
  const panelRef   = useRef(null);

  const { todayData = [], earlierData = [], unReadCount = 0 } = useSelector(s => s.notification);
  const [open, setOpen] = useState(false);

  useEffect(() => { dispatch(getGlobalNotifications()); }, [dispatch]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        panelRef.current  && !panelRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkAll = async (e) => {
    e.preventDefault();
    try {
      await dispatch(markAllReadNotifications()).unwrap();
    } catch { toast.error('Failed to mark as read.'); }
  };

  const handleNavigate = (url) => {
    setOpen(false);
    // If it's an absolute URL, extract the path
    try {
      const parsed = new URL(url);
      navigate(parsed.pathname + parsed.search);
    } catch {
      // Relative path
      navigate(url);
    }
  };

  const totalCount = todayData.length + earlierData.length;

  return (
    <div className="tw-relative" style={{ listStyle: 'none' }}>
      {/* Bell button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        className="ptl-tap tw-relative tw-w-9 tw-h-9 tw-flex tw-items-center tw-justify-center tw-rounded tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-slate-500 hover:tw-text-slate-800 hover:tw-bg-slate-100 tw-transition-colors"
      >
        <FaBell size={16} />
        {unReadCount > 0 && (
          <span className="tw-absolute -tw-top-0.5 -tw-right-0.5 tw-min-w-[18px] tw-h-[18px] tw-rounded-xl tw-bg-orange-500 tw-text-white tw-text-[9px] tw-font-bold tw-flex tw-items-center tw-justify-center tw-px-1 tw-border-2 tw-border-white">
            {unReadCount > 99 ? '99+' : unReadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel.
          A 360px panel anchored to the bell runs off the left edge of a phone,
          which cut the header and every notification in half. Below `sm` it is
          pinned to the viewport with a gutter on both sides instead; from `sm`
          up it is the same dropdown as before. */}
      {open && (
        <div
          ref={panelRef}
          className="tw-fixed tw-left-3 tw-right-3 tw-top-[4.25rem] sm:tw-absolute sm:tw-left-auto sm:tw-right-0 sm:tw-top-11 sm:tw-w-[360px] tw-bg-white tw-rounded-2xl tw-shadow-xl tw-border tw-border-slate-200 tw-overflow-hidden tw-z-[9999]"
        >
          {/* Header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-slate-100">
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-text-sm tw-font-bold tw-text-slate-800">Notifications</span>
              {unReadCount > 0 && (
                <span className="tw-text-[10px] tw-font-bold tw-bg-orange-100 tw-text-orange-600 tw-px-1.5 tw-py-0.5 tw-rounded-xl">
                  {unReadCount} new
                </span>
              )}
            </div>
            {unReadCount > 0 && (
              <button type="button" onClick={handleMarkAll}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-blue-500 hover:tw-text-blue-700 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-font-medium">
                <FaCheckDouble size={10} /> Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="tw-overflow-y-auto tw-divide-y tw-divide-slate-100 tw-max-h-[min(400px,calc(100dvh-11rem))]">
            {totalCount === 0 ? (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-text-slate-300 tw-gap-3">
                <FaBell size={28} />
                <p className="tw-text-sm tw-text-slate-400">No notifications</p>
              </div>
            ) : (
              <>
                {todayData.length > 0 && (
                  <>
                    <div className="tw-px-4 tw-py-1.5 tw-bg-slate-50">
                      <span className="tw-text-[9px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-400">Today</span>
                    </div>
                    {todayData.map(n => (
                      <NotificationItem key={n.id} {...n} onNavigate={handleNavigate} />
                    ))}
                  </>
                )}
                {earlierData.length > 0 && (
                  <>
                    <div className="tw-px-4 tw-py-1.5 tw-bg-slate-50">
                      <span className="tw-text-[9px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-400">Earlier</span>
                    </div>
                    {earlierData.map(n => (
                      <NotificationItem key={n.id} {...n} onNavigate={handleNavigate} />
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="tw-border-t tw-border-slate-100 tw-px-4 tw-py-2.5 tw-text-center">
            <button type="button" onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="tw-text-[12px] tw-text-blue-500 hover:tw-text-blue-700 tw-font-semibold tw-border-0 tw-bg-transparent tw-cursor-pointer">
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
